import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Select,
    Option,
    Typography,
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

const PartsTransactionForm = ({ open, onClose, onSuccess, partId }) => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [activeTab, setActiveTab] = useState('purchase');
    const [formData, setFormData] = useState({
        // Purchase
        supplier_id: '',
        purchase_invoice_no: '',
        purchase_invoice_date: '',
        quantity: '',
        unit_cost: '',
        // Sale
        unit_price: '',
        // Transfer
        from_location: '',
        to_location: '',
        // Adjustment
        quantity_change: '',
        reason: '',
        // Usage
        job_card_id: '',
        // Common
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [suppliers, setSuppliers] = useState([]);

    const fetchSuppliers = useCallback(async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/parts/suppliers/active`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (response.data) {
                setSuppliers(response.data);
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
        }
    }, [authToken]);

    const fetchCashbookAccounts = useCallback(async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/api/cashbook/accounts`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (response.data && response.data.success) {
                // Accounts are fetched but not stored in state as they're not used
            }
        } catch (error) {
            console.error('Error fetching cashbook accounts:', error);
        }
    }, [authToken]);

    useEffect(() => {
        if (open) {
            fetchSuppliers();
            fetchCashbookAccounts();
        }
    }, [open, fetchSuppliers, fetchCashbookAccounts]);

    const validate = () => {
        const newErrors = {};
        if (!formData.date) newErrors.date = 'Date is required';

        if (activeTab === 'purchase') {
            if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
                newErrors.quantity = 'Quantity must be greater than zero';
            }
            if (!formData.unit_cost || parseFloat(formData.unit_cost) <= 0) {
                newErrors.unit_cost = 'Unit cost must be greater than zero';
            }
        } else if (activeTab === 'sale') {
            if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
                newErrors.quantity = 'Quantity must be greater than zero';
            }
            if (!formData.unit_price || parseFloat(formData.unit_price) <= 0) {
                newErrors.unit_price = 'Unit price must be greater than zero';
            }
        } else if (activeTab === 'transfer') {
            if (!formData.to_location) newErrors.to_location = 'To location is required';
            if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
                newErrors.quantity = 'Quantity must be greater than zero';
            }
        } else if (activeTab === 'adjustment') {
            if (!formData.quantity_change) {
                newErrors.quantity_change = 'Quantity change is required';
            }
        } else if (activeTab === 'usage') {
            if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
                newErrors.quantity = 'Quantity must be greater than zero';
            }
            if (!formData.unit_cost || parseFloat(formData.unit_cost) <= 0) {
                newErrors.unit_cost = 'Unit cost must be greater than zero';
            }
            if (!formData.job_card_id) newErrors.job_card_id = 'Job card ID is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            let endpoint = '';
            let payload = {};

            if (activeTab === 'purchase') {
                endpoint = `${rootUrl}/api/parts/transactions/purchase`;
                payload = {
                    part_id: partId,
                    supplier_id: formData.supplier_id || null,
                    purchase_invoice_no: formData.purchase_invoice_no || null,
                    purchase_invoice_date: formData.purchase_invoice_date || null,
                    quantity: parseInt(formData.quantity),
                    unit_cost: parseFloat(formData.unit_cost),
                    input_by: user?.username || 'system',
                    notes: formData.notes || null
                };
            } else if (activeTab === 'sale') {
                endpoint = `${rootUrl}/api/parts/transactions/sale`;
                payload = {
                    part_id: partId,
                    quantity: parseInt(formData.quantity),
                    unit_price: parseFloat(formData.unit_price),
                    input_by: user?.username || 'system',
                    notes: formData.notes || null
                };
            } else if (activeTab === 'transfer') {
                endpoint = `${rootUrl}/api/parts/transactions/transfer`;
                payload = {
                    part_id: partId,
                    quantity: parseInt(formData.quantity),
                    from_location: formData.from_location || null,
                    to_location: formData.to_location,
                    input_by: user?.username || 'system',
                    notes: formData.notes || null
                };
            } else if (activeTab === 'adjustment') {
                endpoint = `${rootUrl}/api/parts/transactions/adjustment`;
                payload = {
                    part_id: partId,
                    quantity_change: parseInt(formData.quantity_change),
                    reason: formData.reason || null,
                    input_by: user?.username || 'system',
                    notes: formData.notes || null
                };
            } else if (activeTab === 'usage') {
                endpoint = `${rootUrl}/api/parts/transactions/usage`;
                payload = {
                    part_id: partId,
                    quantity: parseInt(formData.quantity),
                    unit_cost: parseFloat(formData.unit_cost),
                    job_card_id: formData.job_card_id,
                    input_by: user?.username || 'system',
                    notes: formData.notes || null
                };
            }

            const response = await axios.post(
                endpoint,
                payload,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                onSuccess && onSuccess(response.data.data);
                handleClose();
            } else {
                setErrors({ submit: response.data?.message || 'Failed to create transaction' });
            }
        } catch (error) {
            setErrors({ submit: error.response?.data?.message || 'Error creating transaction' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            supplier_id: '',
            purchase_invoice_no: '',
            purchase_invoice_date: '',
            quantity: '',
            unit_cost: '',
            unit_price: '',
            from_location: '',
            to_location: '',
            quantity_change: '',
            reason: '',
            job_card_id: '',
            date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setErrors({});
        setActiveTab('purchase');
        onClose();
    };

    return (
        <Dialog open={open} handler={handleClose} size="lg">
            <DialogHeader>Record Parts Transaction</DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody>
                    <Tabs value={activeTab} onChange={setActiveTab}>
                        <TabsHeader>
                            <Tab value="purchase">Purchase</Tab>
                            <Tab value="sale">Sale</Tab>
                            <Tab value="transfer">Transfer</Tab>
                            <Tab value="adjustment">Adjustment</Tab>
                            <Tab value="usage">Usage</Tab>
                        </TabsHeader>
                        <TabsBody>
                            <TabPanel value="purchase">
                                <div className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Select
                                            label="Supplier"
                                            value={formData.supplier_id}
                                            onChange={(val) => setFormData(prev => ({ ...prev, supplier_id: val }))}
                                        >
                                            <Option value="">Select Supplier</Option>
                                            {suppliers.map(supplier => (
                                                <Option key={supplier.id} value={supplier.id}>
                                                    {supplier.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        <Input
                                            type="date"
                                            label="Invoice Date"
                                            value={formData.purchase_invoice_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, purchase_invoice_date: e.target.value }))}
                                        />
                                    </div>
                                    <Input
                                        label="Invoice Number"
                                        value={formData.purchase_invoice_no}
                                        onChange={(e) => setFormData(prev => ({ ...prev, purchase_invoice_no: e.target.value }))}
                                    />
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            type="number"
                                            label="Quantity *"
                                            value={formData.quantity}
                                            onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                                            error={!!errors.quantity}
                                        />
                                        <Input
                                            type="number"
                                            label="Unit Cost *"
                                            value={formData.unit_cost}
                                            onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: e.target.value }))}
                                            error={!!errors.unit_cost}
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel value="sale">
                                <div className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            type="number"
                                            label="Quantity *"
                                            value={formData.quantity}
                                            onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                                            error={!!errors.quantity}
                                        />
                                        <Input
                                            type="number"
                                            label="Unit Price *"
                                            value={formData.unit_price}
                                            onChange={(e) => setFormData(prev => ({ ...prev, unit_price: e.target.value }))}
                                            error={!!errors.unit_price}
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel value="transfer">
                                <div className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label="From Location"
                                            value={formData.from_location}
                                            onChange={(e) => setFormData(prev => ({ ...prev, from_location: e.target.value }))}
                                        />
                                        <Input
                                            label="To Location *"
                                            value={formData.to_location}
                                            onChange={(e) => setFormData(prev => ({ ...prev, to_location: e.target.value }))}
                                            error={!!errors.to_location}
                                        />
                                    </div>
                                    <Input
                                        type="number"
                                        label="Quantity *"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                                        error={!!errors.quantity}
                                    />
                                </div>
                            </TabPanel>

                            <TabPanel value="adjustment">
                                <div className="space-y-4 mt-4">
                                    <Input
                                        type="number"
                                        label="Quantity Change *"
                                        value={formData.quantity_change}
                                        onChange={(e) => setFormData(prev => ({ ...prev, quantity_change: e.target.value }))}
                                        error={!!errors.quantity_change}
                                        helperText="Positive for increase, negative for decrease"
                                    />
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                                        <textarea
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            value={formData.reason}
                                            onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel value="usage">
                                <div className="space-y-4 mt-4">
                                    <Input
                                        label="Job Card ID *"
                                        value={formData.job_card_id}
                                        onChange={(e) => setFormData(prev => ({ ...prev, job_card_id: e.target.value }))}
                                        error={!!errors.job_card_id}
                                    />
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            type="number"
                                            label="Quantity *"
                                            value={formData.quantity}
                                            onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                                            error={!!errors.quantity}
                                        />
                                        <Input
                                            type="number"
                                            label="Unit Cost *"
                                            value={formData.unit_cost}
                                            onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: e.target.value }))}
                                            error={!!errors.unit_cost}
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </TabPanel>
                        </TabsBody>
                    </Tabs>

                    <div className="mt-4 space-y-4">
                        <Input
                            type="date"
                            label="Date *"
                            value={formData.date}
                            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                            error={!!errors.date}
                        />
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                            <textarea
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                rows={2}
                            />
                        </div>
                    </div>

                    {errors.submit && (
                        <Typography color="red" variant="small" className="mt-4">
                            {errors.submit}
                        </Typography>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={handleClose}
                        className="mr-2"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="blue"
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
};

export default PartsTransactionForm;

